// src/index.js
import React, { useEffect, useState, useRef } from "react";
import { createRoot } from "react-dom/client";
import { Provider, useSelector, useDispatch } from "react-redux";
import { BrowserRouter, useNavigate, useLocation } from "react-router-dom";
import App from "./App";
import { store } from "./app/store";
import {
  allLocations,
  getLocations,
  locationsStatus,
  initialLocation
} from "./features/locations/locationSlice";
import { uid as selectUid } from "./features/user/userSlice";
import reportWebVitals from "./reportWebVitals";
import "./index.css";

// Simple full-screen loader while bootstrapping
const Splash = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      fontSize: 18
    }}
  >
    Loading...
  </div>
);

// Encapsulate initial fetch + route decision here
const BootstrapGate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [ready, setReady] = useState(false);
  const hasNavigated = useRef(false); // Prevent multiple navigations

  // Selectors expected from your slices
  const uid = useSelector(selectUid);
  const locations = useSelector(allLocations);
  const status = useSelector(locationsStatus);
  const iniLocation = useSelector(initialLocation);

  // Kick off initial locations load once
  useEffect(() => {
    if (status === "idle") {
      dispatch(getLocations());
    }
  }, [dispatch, status]);

  // Decide the initial route when we have enough info
  useEffect(() => {
    // Still loading: keep splash
    if (status === "idle" || status === "loading") return;

    // Prevent multiple navigation attempts
    if (hasNavigated.current) {
      setReady(true);
      return;
    }

    // On success or failure, make a decision:
    const isLoggedIn = Boolean(uid);
    const hasSingleLocation =
      Array.isArray(locations) && locations.length === 1;
    const hasInitLocation = Object.keys(iniLocation).length > 0;

    if (isLoggedIn || hasSingleLocation || hasInitLocation) {
      if (location.pathname !== "/products") {
        hasNavigated.current = true;
        navigate("/products", { replace: true });
      }
    } else {
      // Otherwise, let index render SelectLocation at "/"
      if (location.pathname !== "/") {
        hasNavigated.current = true;
        navigate("/", { replace: true });
      }
    }

    setReady(true);
  }, [uid, locations, status, navigate, location.pathname]);

  if (!ready) return <Splash />;

  // Once ready, mount the app and let routes render
  return <App />;
};

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <Provider store={store}>
    <BrowserRouter>
      <BootstrapGate />
    </BrowserRouter>
  </Provider>
);

reportWebVitals();
